import React from 'react';
import { Typography, COLORS, Tooltip, IconButton, Tag } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Folder from '@mui/icons-material/Folder';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import type { Category } from 'types';
import { LIBRARY_GRID_CARD_SX } from 'constants/styles';

interface FolderGridCardProps {
  folder: Category;
  onOpen: (folder: Category, e?: React.MouseEvent) => void;
  onEdit: (folder: Category) => void;
  onDelete: (folder: Category) => void;
  canEdit: boolean;
  canDelete: boolean;
}

const REVEAL_SX = { opacity: 0, transition: 'opacity 0.15s ease' } as const;

/** A folder card in the grid view of the browse and home pages. */
const FolderGridCard: React.FC<FolderGridCardProps> = ({
  folder,
  onOpen,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const displayName = folder.name;

  return (
  <Box
    role="button"
    tabIndex={0}
    onClick={(e: React.MouseEvent) => onOpen(folder, e)}
    onKeyDown={(e: React.KeyboardEvent) => {
      // Only act when the card itself is focused, so Enter/Space on a nested
      // Edit/Delete button doesn't bubble up and also open the folder.
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen(folder);
      }
    }}
    sx={{ ...LIBRARY_GRID_CARD_SX, height: 108 }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, height: 44, mb: 1 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: COLORS.blue[100], color: COLORS.blue[700], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Folder style={{ fontSize: 20 }} />
      </Box>
      <Typography
        variant="interactive-sm"
        style={{ fontWeight: 600, color: COLORS.gray[900], display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35 }}
      >
        {displayName}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pt: 1 }}>
      <Tag variant="default" label="Folder" size="small" />
      <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
        {canEdit && (
          <Tooltip title="Edit folder" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" className="info-reveal" sx={REVEAL_SX} aria-label="Edit folder" onClick={() => onEdit(folder)}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete folder" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" className="info-reveal" sx={REVEAL_SX} aria-label="Delete folder" onClick={() => onDelete(folder)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  </Box>
  );
};

export default React.memo(FolderGridCard);
